import React, { Component, ChangeEvent, ReactNode, FormEvent } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import { NewPasswordChallenge, AuthHelper, AuthToken, AuthChallenge } from '../../helpers/AuthHelper';

interface PasswordUpdateFormProps {
  newPasswordChallenge?: NewPasswordChallenge;
  onUpdated?: (authToken?: AuthToken|AuthChallenge) => void;
  onReturn?: () => void;
  alignLeft?: boolean;
  alignRight?: boolean;
}

interface PasswordUpdateFormState {
  currentPassword: string;
  password: string;
  passwordRepeat: string;
  currentPasswordError?: string;
  passwordError?: string;
  passwordRepeatError?: string;
  updateError?: string;
}

export const PASSWORD_SPECIAL_CHARS = ['^', '$', '*', '.', '[', ']', '{', '}', '(', ')', '?', '-', '"', '!', '@', '#', '%', '&', '/', '\\', ',', '>', '<', '\'', ':', ';', '|', '_', '~', '`', '+', '='];
export const MIN_PASSWORD_LENGTH = 12;

export function PasswordValidInfo({ password }: { password: string }): JSX.Element {
  const pwInvalidLength = password.length < MIN_PASSWORD_LENGTH;
  const pwMissingLowercase = /[a-z]/.test(password) === false;
  const pwMissingUppercase = /[A-Z]/.test(password) === false;
  const pwMissingNumber = /[0-9]/.test(password) === false;
  const pwMissingSpecialChar = PASSWORD_SPECIAL_CHARS.some(char => password.includes(char)) === false;
  const invalidPassword = pwInvalidLength || pwMissingLowercase || pwMissingUppercase || pwMissingNumber || pwMissingSpecialChar;

  if (!invalidPassword) {
    return <></>;
  }

  return <div className="password-valid-info">
    Je nieuwe wachtwoord voldoet nog niet aan de wachtwoordeisen:
    <ul>
      { pwInvalidLength && <li>Tenminste {MIN_PASSWORD_LENGTH} karakters</li> }
      { pwMissingLowercase && <li>Minimaal 1 letter <code>a-z</code></li> }
      { pwMissingUppercase && <li>Minimaal 1 hoofdletter <code>A-Z</code></li> }
      { pwMissingNumber && <li>Minimaal 1 cijfer <code>0-9</code></li> }
      { pwMissingSpecialChar && <li>Minimaal 1 speciaal teken <code>{PASSWORD_SPECIAL_CHARS.join('')}</code></li> }
    </ul>
  </div>;
}

export class PasswordUpdateForm extends Component<PasswordUpdateFormProps, PasswordUpdateFormState> {
  private static readonly INPUT_CURRENT_PASSWORD = 'current_password';
  private static readonly INPUT_PASSWORD = 'password';
  private static readonly INPUT_PASSWORD_REPEAT = 'password_repeat';
  private static readonly MIN_AWS_PASSWORD_LENGTH = 6;
  // About MIN_AWS_PASSWORD_LENGTH: You would assume that this would be equal to MIN_PASSWORD_LENGTH
  // which is also set in AWS. But it's not, AWS will reject some requests them if the password is
  // less than 6 characters, even tough you cannot set passwords shorter than MIN_PASSWORD_LENGTH.
  // So we just enforce this 6 character minimum for password input so the user does not get any weird
  // errors.

  constructor(props: PasswordUpdateFormProps) {
    super(props);
    this.state = {
      currentPassword: '',
      password: '',
      passwordRepeat: ''
    };
  }

  handleInputChange = (event: ChangeEvent): void => {
    const { name, value } = (event.target as HTMLInputElement);
    switch (name) {
      case PasswordUpdateForm.INPUT_CURRENT_PASSWORD:
        this.setState({ currentPassword: value });
        break;
      case PasswordUpdateForm.INPUT_PASSWORD:
        this.setState({ password: value });
        break;
      case PasswordUpdateForm.INPUT_PASSWORD_REPEAT:
        this.setState({ passwordRepeat: value });
        break;
    }
  };

  resetErrors = (): void => {
    this.setState({
      currentPasswordError: undefined,
      passwordError: undefined,
      passwordRepeatError: undefined,
      updateError: undefined,
    });
  };

  validate = (password: string, passwordRepeat: string, currentPassword?: string): boolean => {
    if (currentPassword && currentPassword.length < PasswordUpdateForm.MIN_AWS_PASSWORD_LENGTH) {
      this.setState({ currentPasswordError: 'Vul je huidige wachtwoord in' });
      return false;
    }
    if (!password || password.length === 0) {
      this.setState({ passwordError: 'Vul een nieuw wachtwoord in' });
      return false;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      this.setState({ passwordError:
          `Het wachtwoord dient minimaal ${MIN_PASSWORD_LENGTH} tekens lang te zijn` });
      return false;
    }
    if (password !== passwordRepeat) {
      this.setState({ passwordRepeatError: 'De ingevulde wachtwoorden komen niet overeen' });
      return false;
    }
    return true;
  };

  handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    this.resetErrors();
    const { newPasswordChallenge } = this.props;

    const { password, passwordRepeat } = this.state;
    const currentPassword = !newPasswordChallenge ? this.state.currentPassword : undefined;
    const isValid = this.validate(password, passwordRepeat, currentPassword);
    if (!isValid) {
      return;
    }

    let request: Promise<AuthToken|AuthChallenge|undefined>;
    if (!newPasswordChallenge) {
      request = AuthHelper.changePassword(currentPassword as string, password);
    } else {
      request = AuthHelper.handleNewPasswordChallenge(password, newPasswordChallenge);
    }

    request.then(result => {
      if (this.props.onUpdated) {
        this.props.onUpdated(result);
      }
    })
      .catch(err => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        switch (err.code) {
          case 'LimitExceededException':
            this.setState({
              updateError: 'Er zijn te veel pogingen gedaan in een korte tijd, probeer het over vijf minuten opnieuw'
            });
            break;
          case 'NotAuthorizedException':
            this.setState({ updateError: 'Het huidige wachtwoord is niet correct' });
            break;
          case 'InvalidPasswordException':
            this.setState({
              updateError: 'Het wachtwoord moet minimaal 1 hoofdletter, 1 kleine letter, 1 cijfer en 1 leesteken bevatten'
            });
            break;
          default:
            this.setState({ updateError: 'Wachtwoord wijzigen is mislukt door een onverwachte fout' });
            console.error('Unexpected auth response:', err);
            break;
        }
      });
  };

  handleReturn = (): void => {
    if (this.props.onReturn) {
      this.props.onReturn();
    }
  };

  render(): ReactNode {
    const { onReturn, alignLeft, alignRight, newPasswordChallenge } = this.props;
    const { password, currentPasswordError, passwordError, passwordRepeatError, updateError } = this.state;
    const fullClassName = `form ${alignLeft ? 'form--left' : alignRight ? 'form--right' : ''}`;

    return (
      <>
        <Form className={fullClassName} onSubmit={this.handleSubmit}>
          {
            !newPasswordChallenge &&
            <Form.Group className="form__item">
              <Form.Label>Huidig wachtwoord</Form.Label>
              <Form.Control
                name={PasswordUpdateForm.INPUT_CURRENT_PASSWORD}
                type="password"
                autoComplete="current-password"
                onChange={this.handleInputChange}
                isInvalid={!!currentPasswordError}
              />
              {currentPasswordError && <Alert variant="danger">{currentPasswordError}</Alert> }
            </Form.Group>
          }
          <Form.Group className="form__item">
            <Form.Label>Nieuw wachtwoord</Form.Label>
            <Form.Control
              name={PasswordUpdateForm.INPUT_PASSWORD}
              type="password"
              autoComplete="new-password"
              onChange={this.handleInputChange}
              isInvalid={!!passwordError}
            />
            <Form.Text className="text-muted">
              <PasswordValidInfo password={password} />
            </Form.Text>
            { passwordError && <Alert variant="danger">{passwordError}</Alert> }
          </Form.Group>

          <Form.Group className="form__item">
            <Form.Label>Herhaal wachtwoord</Form.Label>
            <Form.Control
              name={PasswordUpdateForm.INPUT_PASSWORD_REPEAT}
              type="password"
              autoComplete="new-password"
              onChange={this.handleInputChange}
              isInvalid={!!passwordRepeatError}
            />
            {passwordRepeatError && <Alert variant="danger">{passwordRepeatError}</Alert> }
          </Form.Group>

          {updateError && <Alert variant="danger">{updateError}</Alert> }
          <Button
            className={`form__submit ${alignLeft ? 'form__submit--left' : alignRight ? 'form__submit--right' : ''}`}
            variant="primary" type="submit">Toepassen</Button>
          {
            onReturn &&
            <Button className="form__link" variant="link" onClick={this.handleReturn}>
              Ga terug
            </Button>
          }
        </Form>
      </>
    );
  }
}
